import React, { useEffect, useRef, useState } from 'react';
import { FiBold, FiItalic, FiLink, FiUnderline } from 'react-icons/fi';
import { MdFormatStrikethrough } from 'react-icons/md';

const inputClass =
    'mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-3';

function escapeHrefAttr(url) {
    return String(url || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

const TelegramCaptionEditor = ({
    value,
    onChange,
    placeholder,
    rows = 8,
    disabled = false,
    label = 'Caption',
    maxLength = null,
}) => {
    const textareaRef = useRef(null);
    const [selection, setSelection] = useState({ start: 0, end: 0 });
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [linkError, setLinkError] = useState('');
    const [pendingSelection, setPendingSelection] = useState(null);
    const linkInputRef = useRef(null);

    function rememberSelection() {
        const el = textareaRef.current;
        if (!el) return { start: 0, end: 0 };
        const next = { start: el.selectionStart, end: el.selectionEnd };
        setSelection(next);
        return next;
    }

    function getSelectedText(sel = selection) {
        return String(value || '').slice(sel.start, sel.end);
    }

    function applyWrap(openTag, closeTag, selOverride = null) {
        const el = textareaRef.current;
        const sel = selOverride || rememberSelection();
        if (sel.start === sel.end) return false;

        const text = String(value || '');
        const selected = text.slice(sel.start, sel.end);
        const next = text.slice(0, sel.start) + openTag + selected + closeTag + text.slice(sel.end);
        if (maxLength && next.length > maxLength) return false;
        onChange(next);

        const cursorStart = sel.start + openTag.length;
        const cursorEnd = cursorStart + selected.length;
        requestAnimationFrame(() => {
            if (!el) return;
            el.focus();
            el.setSelectionRange(cursorStart, cursorEnd);
            setSelection({ start: cursorStart, end: cursorEnd });
        });
        return true;
    }

    function handleFormat(tag) {
        const sel = rememberSelection();
        if (sel.start === sel.end) return;
        applyWrap(`<${tag}>`, `</${tag}>`, sel);
    }

    function handleLinkClick() {
        const sel = rememberSelection();
        if (sel.start === sel.end) return;
        setPendingSelection(sel);
        setLinkUrl('');
        setLinkError('');
        setShowLinkModal(true);
    }

    function handleCloseLinkModal() {
        setShowLinkModal(false);
        setLinkUrl('');
        setLinkError('');
        setPendingSelection(null);
        requestAnimationFrame(() => textareaRef.current?.focus());
    }

    function handleConfirmLink() {
        const url = String(linkUrl || '').trim();
        if (!url) {
            setLinkError('Enter a URL.');
            return;
        }
        if (!/^https?:\/\//i.test(url)) {
            setLinkError('Use http(s)://…');
            return;
        }
        const sel = pendingSelection || selection;
        if (!sel || sel.start === sel.end) {
            setLinkError('Select text first.');
            return;
        }
        applyWrap(`<a href="${escapeHrefAttr(url)}">`, '</a>', sel);
        handleCloseLinkModal();
    }

    useEffect(() => {
        if (showLinkModal) {
            requestAnimationFrame(() => linkInputRef.current?.focus());
        }
    }, [showLinkModal]);

    const hasSelection = selection.start !== selection.end && getSelectedText().length > 0;
    const toolbarBtn =
        'inline-flex h-8 items-center justify-center gap-1 rounded border border-gray-300 bg-white px-2 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40';
    const iconBtn = `${toolbarBtn} w-8 px-0`;

    return (
        <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
            <div className="mb-1 flex flex-wrap items-center gap-1">
                <button type="button" title="Bold" className={iconBtn} disabled={disabled || !hasSelection} onMouseDown={(e) => e.preventDefault()} onClick={() => handleFormat('b')}>
                    <FiBold className="h-4 w-4" />
                </button>
                <button type="button" title="Italic" className={iconBtn} disabled={disabled || !hasSelection} onMouseDown={(e) => e.preventDefault()} onClick={() => handleFormat('i')}>
                    <FiItalic className="h-4 w-4" />
                </button>
                <button type="button" title="Underline" className={iconBtn} disabled={disabled || !hasSelection} onMouseDown={(e) => e.preventDefault()} onClick={() => handleFormat('u')}>
                    <FiUnderline className="h-4 w-4" />
                </button>
                <button type="button" title="Strikethrough" className={iconBtn} disabled={disabled || !hasSelection} onMouseDown={(e) => e.preventDefault()} onClick={() => handleFormat('s')}>
                    <MdFormatStrikethrough className="h-4 w-4" />
                </button>
                <button type="button" title="Link" className={iconBtn} disabled={disabled || !hasSelection} onMouseDown={(e) => e.preventDefault()} onClick={handleLinkClick}>
                    <FiLink className="h-4 w-4" />
                </button>
                <span className="ml-1 text-xs text-gray-500">Select text to format / link.</span>
            </div>

            <textarea
                ref={textareaRef}
                rows={rows}
                value={value}
                disabled={disabled}
                maxLength={maxLength || undefined}
                onChange={(e) => {
                    const next = e.target.value;
                    if (maxLength && next.length > maxLength) {
                        onChange(next.slice(0, maxLength));
                        return;
                    }
                    onChange(next);
                }}
                onSelect={rememberSelection}
                onKeyUp={rememberSelection}
                onClick={rememberSelection}
                placeholder={placeholder}
                className={`${inputClass} font-normal font-mono text-sm`}
            />
            {maxLength ? (
                <p className={`mt-1 text-xs ${String(value || '').length >= maxLength ? 'font-semibold text-red-600' : 'text-gray-500'}`}>
                    {String(value || '').length}/{maxLength}
                </p>
            ) : null}

            {showLinkModal ? (
                <div className="fixed inset-0 z-[210] flex items-start justify-center bg-gray-900/50 px-4 pt-24">
                    <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
                        <h3 className="mb-1 text-base font-semibold text-gray-900">Insert link</h3>
                        <p className="mb-4 text-sm text-gray-600">
                            Selected:{' '}
                            <span className="font-medium text-gray-800">
                                “{getSelectedText(pendingSelection || selection) || '…'}”
                            </span>
                        </p>
                        <label className="block text-sm font-medium text-gray-700">URL</label>
                        <input
                            ref={linkInputRef}
                            type="url"
                            value={linkUrl}
                            onChange={(e) => {
                                setLinkUrl(e.target.value);
                                setLinkError('');
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleConfirmLink();
                                }
                                if (e.key === 'Escape') {
                                    e.preventDefault();
                                    handleCloseLinkModal();
                                }
                            }}
                            className={inputClass}
                            placeholder="https://…"
                        />
                        {linkError ? <p className="mt-2 text-sm text-red-600">{linkError}</p> : null}
                        <div className="mt-5 flex justify-end gap-2">
                            <button type="button" onClick={handleCloseLinkModal} className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200">
                                Cancel
                            </button>
                            <button type="button" onClick={handleConfirmLink} className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
                                Apply link
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default TelegramCaptionEditor;
